import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authApi } from '../services/api';
import { announceSessionExpired } from '../services/sessionEvents';
import type { User, UserRole } from '../types';

/** Client-readable cookie set by backend on login so we skip /auth/me when not logged in */
const SESSION_HINT_COOKIE = 'tender_session';

function hasSessionHint(): boolean {
  if (typeof document === 'undefined' || !document.cookie) return false;
  return document.cookie.split(';').some((s) => s.trim().startsWith(SESSION_HINT_COOKIE + '='));
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  hasRole: (...roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // getCsrf runs in background — login is CSRF-exempt, but other POST actions need it
      authApi.getCsrf().catch(() => {});
      if (!hasSessionHint()) {
        setIsLoading(false);
        return;
      }
      try {
        // 401 бол axios interceptor автоматаар refresh хийж retry хийнэ
        const me = await authApi.getMe();
        setUser(me as User);
      } catch {
        // Refresh ч амжилтгүй бол interceptor /login руу шилжүүлнэ
        document.cookie = 'tender_session=; path=/; max-age=0';
        setUser(null);
      }
      setIsLoading(false);
    };
    initAuth();
  }, []);

  // Proactive token refresh — renew before 60 min expiry + on tab re-focus.
  // Each successful refresh also rotates the (7-day) refresh token, so a
  // session stays alive indefinitely as long as the tab is used at least
  // once every ~45 minutes — well past the 6h "stay active → stay logged in"
  // bar, with no separate cap needed.
  useEffect(() => {
    if (!user) return;
    // a 401 here = the refresh session is gone: say so now, not at the next failing click
    const doRefresh = () => authApi.refresh().catch((e: { response?: { status?: number } }) => {
      if (e?.response?.status === 401) announceSessionExpired();
    });
    const id = setInterval(doRefresh, 45 * 60 * 1000);
    // Browser throttles timers in background tabs — refresh when user comes back
    const onVisible = () => {
      if (document.visibilityState === 'visible') doRefresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user]);

  // «Онлайн»: while the tab is open and visible, tell the backend every 2 min that the user is
  // here (it stamps users.last_seen_at) — an idle open page makes no other requests
  useEffect(() => {
    if (!user) return;
    const ping = () => {
      if (document.visibilityState === 'visible') authApi.getMe().catch(() => {});
    };
    const id = setInterval(ping, 2 * 60 * 1000);
    return () => clearInterval(id);
  }, [user]);

  const login = useCallback(async (username: string, password: string) => {
    const response = await authApi.login(username, password);
    setUser(response.user as User);
    // Refresh CSRF token after login so POST/PUT/DELETE actions work immediately
    await authApi.getCsrf().catch(() => {});
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Backend logout may fail (network, CSRF, timeout) — ignore
    }
    // Always clear cookies + state regardless of backend result
    document.cookie = 'tender_session=; path=/; max-age=0';
    document.cookie = 'csrf_token=; path=/; max-age=0';
    setUser(null);
  }, []);

  const hasRole = useCallback((...roles: UserRole[]) => {
    if (!user) return false;
    return roles.includes(user.role);
  }, [user]);

  const value = useMemo(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    hasRole,
  }), [user, isLoading, login, logout, hasRole]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
